import { Env } from '@env';
import { Stack, useRouter } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useBulkListCars } from '@/api/partners/cars/use-bulk-list-cars';
import { useBulkListDefaults } from '@/api/partners/cars/use-bulk-list-defaults';
import {
  trimsForVin,
  useCarDetailOptions,
} from '@/api/partners/cars/use-car-detail-options';
import { useCarProjectedMonthlyIncomes } from '@/api/partners/cars/use-cars-projected-monthly-incomes';
import { useNeedsSetupCars } from '@/api/partners/cars/use-needs-setup-cars';
import { useCreateLocation } from '@/api/partners/locations/use-create-location';
import { useLocations } from '@/api/partners/locations/use-locations';
import BackButton from '@/components/back-button';
import type { AddLocationFormProps } from '@/components/cars/add-location-form';
import AddLocationForm from '@/components/cars/add-location-form';
import type { CarDetailsFormType } from '@/components/cars/bulk-list/car-details-form';
import CarDetailsForm from '@/components/cars/bulk-list/car-details-form';
import type { CarPricingFormType } from '@/components/cars/bulk-list/car-pricing-form';
import CarPricingForm from '@/components/cars/bulk-list/car-pricing-form';
import type { DefaultsFormType } from '@/components/cars/bulk-list/defaults-form';
import DefaultsForm from '@/components/cars/bulk-list/defaults-form';
import { HowAutomatedPricingWorksModal } from '@/components/cars/bulk-list/how-automated-pricing-works-modal';
import { WhyGloveboxPinModal } from '@/components/cars/setup/why-glovebox-pin-modal';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop, snakeToCamelCase, snakeToTitleCase } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { clearByPrefix, usePersistedState } from '@/core/storage';
import { Button, showErrorMessage, Text } from '@/ui';
import type { DropdownOption } from '@/ui/dropdown';
import { Loader } from '@/ui/loader';
import { ProgressIndicator } from '@/ui/progress-indicator';

enum Step {
  BULK_LIST = 0,
  ADD_LOCATION = 1,
  SELECT_CARS = 2,
  PRICING = 3,
}

const Steps = [
  {
    step: Step.BULK_LIST,
    title: 'Bulk List Defaults',
  },
  {
    step: Step.ADD_LOCATION,
    title: 'Add Location',
  },
  {
    step: Step.SELECT_CARS,
    title: 'Select Cars',
  },
  {
    step: Step.PRICING,
    title: 'Set Your Pricing',
  },
];

export default function BulkList() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();

  const draftPrefix = 'owner-bulk-list-draft:';
  const k = (name: string) => `${draftPrefix}${name}`;

  const [currentStep, setCurrentStep] = usePersistedState(
    k('currentStep'),
    Step.BULK_LIST
  );
  const [cars, setCars] = useState<Car[]>([]);
  const [bulkListFormValues, setBulkListFormValues] = usePersistedState<
    DefaultsFormType | undefined
  >(k('bulkListFormValues'), undefined);
  const [bulkListFormValid, setBulkListFormValid] = useState<boolean>(false);
  const [addLocationFormValues, setAddLocationFormValues] = usePersistedState<
    AddLocationFormProps | undefined
  >(k('addLocationFormValues'), undefined);
  const [showAddLocationForm, setShowAddLocationForm] = useState<boolean>(true);
  const [addLocationFormValid, setAddLocationFormValid] =
    useState<boolean>(false);
  const [pickupLocation, setPickupLocation] =
    usePersistedState<DropdownOption | null>(k('pickupLocation'), null);
  const [showWhyGloveboxPinModal, setShowWhyGloveboxPinModal] = useState(false);
  const [
    showHowAutomatedPricingWorksModal,
    setShowHowAutomatedPricingWorksModal,
  ] = useState(false);
  const scrollRef = useRef<KeyboardAwareScrollView>(null);
  const [selectedCarIndexes, setSelectedCarIndexes] = usePersistedState<
    number[]
  >(k('selectedCarIndexes'), []);
  const [carDetailsFormValues, setCarDetailsFormValues] = usePersistedState<
    Record<number, CarDetailsFormType>
  >(k('carDetailsFormValues'), {});
  const [carDetailsFormValidity, setCarDetailsFormValidity] = useState<
    Record<number, boolean>
  >({});
  const [carPricingFormValues, setCarPricingFormValues] = usePersistedState<
    Record<number, CarPricingFormType>
  >(k('carPricingFormValues'), {});
  const [carPricingFormValidity, setCarPricingFormValidity] = useState<
    Record<number, boolean>
  >({});

  const {
    data: bulkListDefaults,
    isPending: isPendingBulkListDefaults,
    refetch: refetchBulkListDefaults,
  } = useBulkListDefaults();

  const { mutateAsync: createLocation, isPending: isCreatingLocation } =
    useCreateLocation();

  const { mutateAsync: bulkListCars, isPending: isBulkListingCars } =
    useBulkListCars();

  const { data: carsNeedSetup, isPending: isLoadingCarsNeedSetup } =
    useNeedsSetupCars({
      enabled: currentStep >= Step.SELECT_CARS,
    });

  const { data: locationsData, refetch: refetchLocations } = useLocations({
    enabled: false,
  });

  const {
    data: projectedMonthlyIncomes,
    isPending: isLoadingProjectedMonthlyIncomes,
  } = useCarProjectedMonthlyIncomes({
    variables: {
      vins: cars
        .filter((car) => selectedCarIndexes.includes(car.id))
        .map((car) => car.vin),
    },
    enabled: currentStep === Step.PRICING,
  });

  const {
    data: carDetailOptions,
    isLoading: isLoadingCarDetailOptions,
    refetch: refetchCarDetailOptions,
  } = useCarDetailOptions({
    variables: {
      vins: cars.map((car) => car.vin),
    },
    enabled: currentStep === Step.SELECT_CARS && cars.length > 0,
  });

  const trimFieldForCar = (car: Car) => {
    if (!carDetailOptions) {
      return { options: [], locked: false };
    }

    const resolved = trimsForVin({
      vin: car.vin,
      trims: carDetailOptions.trims,
      vinTrims: carDetailOptions.vinTrims,
      modelId: car.model?.id,
    });

    return {
      locked: resolved.locked,
      options: resolved.trims.map((trim) => {
        return {
          id: String(trim.id),
          value: String(trim.id),
          title: snakeToTitleCase(trim.name),
        } as DropdownOption;
      }),
    };
  };

  useEffect(() => {
    mixpanelService.track('Bulk Listing Started');
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollToPosition(0, 0, false);
  }, [currentStep]);

  useEffect(() => {
    if (!carsNeedSetup) return;

    setCars(carsNeedSetup);
    const validIds = new Set(carsNeedSetup.map((car) => car.id));
    setSelectedCarIndexes((prev) => {
      const pruned = prev.filter((id) => validIds.has(id));
      return pruned.length > 0 ? pruned : carsNeedSetup.map((car) => car.id);
    });
  }, [carsNeedSetup]);

  const selectedStep = useMemo(() => {
    return Steps.find((step) => step.step === currentStep);
  }, [currentStep]);

  const selectedCars = useMemo(() => {
    return cars.filter((car) => selectedCarIndexes.includes(car.id));
  }, [cars, selectedCarIndexes]);

  const handleAddLocationFormChange = (data: AddLocationFormProps) => {
    setAddLocationFormValues(data);
  };

  const handleAddLocationValidChange = (isValid: boolean) => {
    setAddLocationFormValid(isValid);
  };

  const allCarDetailsFormsValid = useMemo(() => {
    return selectedCarIndexes.every((carId) => {
      return carDetailsFormValidity[carId] === true;
    });
  }, [selectedCarIndexes, carDetailsFormValidity]);

  const handleCarDetailsFormChange = (index, values) => {
    setCarDetailsFormValues((prev) => ({ ...prev, [index]: values }));
  };

  const handleCarDetailsFormValidChange = (index, isValid) => {
    setCarDetailsFormValidity((prev) => ({ ...prev, [index]: isValid }));
  };

  const handleCarPricingFormChange = (index, values) => {
    setCarPricingFormValues((prev) => ({ ...prev, [index]: values }));
  };

  const handleCarPricingFormValidChange = (index, isValid) => {
    setCarPricingFormValidity((prev) => ({ ...prev, [index]: isValid }));
  };

  const allCarPricingFormsValid = useMemo(() => {
    return selectedCarIndexes.every((carId) => {
      return carPricingFormValidity[carId] === true;
    });
  }, [selectedCarIndexes, carPricingFormValidity]);

  const getTimeZone = async ({ lat, lng }: any) => {
    const now = Date.now() / 1000;
    const key = Env.GOOGLE_TIMEZONE_API_KEY;

    const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat}%2C${lng}&timestamp=${now}&key=${key}`;

    const res = await fetch(url, {
      method: 'GET',
    });
    const d = await res.json();

    return d;
  };

  const handleNext = async () => {
    if (currentStep === Step.BULK_LIST) {
      mixpanelService.track('Bulk Listing Step Completed', {
        'Step Name': 'Defaults',
      });
      setCurrentStep(Step.SELECT_CARS);
      return;
    }

    if (currentStep === Step.ADD_LOCATION) {
      const {
        address: addr,
        pickupInstructions,
        dropoffInstructions,
        garageOpenHour,
        garageCloseHour,
        enableGarageHours,
      } = addLocationFormValues!;

      const timeZone = await getTimeZone({
        lat: addr.latitude,
        lng: addr.longitude,
      });

      try {
        const loc = await createLocation({
          address: addr.description,
          zipcode: addr.zipcode,
          city: addr.city,
          state: addr.state,
          latitude: addr.latitude,
          longitude: addr.longitude,
          pickupInstructions,
          dropoffInstructions,
          timeZone: timeZone.timeZoneId,
          garageOpenHour: enableGarageHours ? garageOpenHour : undefined,
          garageCloseHour: enableGarageHours ? garageCloseHour : undefined,
        });

        Toast.show({
          text1: `${addr.description} has been added`,
          type: 'success',
        });
        await refetchLocations();

        setPickupLocation({
          id: String(loc.data.id),
          value: String(loc.data.id),
          title: String(loc.data.id),
        });

        mixpanelService.track('Bulk Listing Step Completed', {
          'Step Name': 'Add Location',
        });
        setCurrentStep(Step.SELECT_CARS);
      } catch (error: any) {
        showErrorMessage(error.message);
      }
      return;
    }

    if (currentStep === Step.SELECT_CARS) {
      mixpanelService.track('Bulk Listing Step Completed', {
        'Step Name': 'Select Cars',
      });
      setCurrentStep(Step.PRICING);
      return;
    }

    if (currentStep === Step.PRICING) {
      let carsToListHash = {};

      selectedCars.forEach((car) => {
        carsToListHash[car.id] = {
          ...bulkListFormValues,
          ...carDetailsFormValues[car.id],
          ...carPricingFormValues[car.id],
          maximumDailyRate: 5000,
        };
      });

      const carsToListArr = Object.entries(carsToListHash).map(
        ([carId, values]) => {
          return {
            id: Number(carId),
            ...values,
          };
        }
      );

      try {
        await bulkListCars({
          cars: carsToListArr,
        });

        mixpanelService.track('Bulk Listing Step Completed', {
          'Step Name': 'Pricing',
        });

        mixpanelService.track('Bulk Listing Completed', {
          'Listed Cars': selectedCars.length,
          'Fixed Daily Rate': Object.values(carPricingFormValues).some(
            (v) => v.useAutomatedPricing === false
          ),
          'Delivery Available': bulkListDefaults?.supportsDelivery,
          'Subscription Available': bulkListDefaults?.allowSubscription,
        });

        clearByPrefix(draftPrefix);

        router.replace({
          pathname: '/partner/cars',
          params: { showCarsListedModal: 'true' },
        });
      } catch (e: any) {
        showErrorMessage(e.message);
      }
    }
  };

  const renderBody = () => {
    if (currentStep === Step.BULK_LIST && bulkListDefaults) {
      return (
        <View style={{ gap: 16 }}>
          <DefaultsForm
            defaults={bulkListDefaults}
            onChange={(values) => {
              setBulkListFormValues(values);
            }}
            onValidChange={(isValid) => {
              setBulkListFormValid(isValid);
            }}
            defaultValues={bulkListFormValues}
          />
        </View>
      );
    }

    if (currentStep === Step.ADD_LOCATION) {
      return (
        <AddLocationForm
          onChange={handleAddLocationFormChange}
          onValidChange={handleAddLocationValidChange}
          defaultValues={addLocationFormValues}
        />
      );
    }

    if (currentStep === Step.SELECT_CARS && carDetailOptions) {
      return (
        <View style={{ flex: 1, gap: 16 }}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Pressable
              onPress={() => {
                if (selectedCarIndexes.length > 0) {
                  setSelectedCarIndexes([]);
                } else {
                  setSelectedCarIndexes(cars.map((car) => car.id));
                }
              }}
            >
              <Text
                type="bodyRegular"
                style={{ textDecorationLine: 'underline' }}
              >
                {selectedCarIndexes.length > 0 ? 'Deselect All' : 'Select All'}
              </Text>
            </Pressable>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {selectedCars.length} of {cars.length} cars selected
            </Text>
          </View>

          {isDesktop() ? (
            <ScrollView
              horizontal
              contentContainerStyle={styles.carDetailsFormContainer}
            >
              {cars.map((car) => {
                const trimField = trimFieldForCar(car);

                return (
                  <CarDetailsForm
                    key={car.id}
                    pickupLocationOptions={carDetailOptions.pickupLocations.map(
                      (loc) => {
                        return {
                          id: String(loc.id),
                          value: String(loc.id),
                          title: loc.address,
                        } as DropdownOption;
                      }
                    )}
                    trimOptions={trimField.options}
                    trimLocked={trimField.locked}
                    licensePlateStateOptions={Object.entries(
                      carDetailOptions.licensePlateStates
                    ).map(
                      ([state, idx]) =>
                        ({
                          id: String(idx),
                          value: state,
                          title:
                            state.charAt(0).toUpperCase() +
                            state.slice(1).replace(/([A-Z])/g, ' $1'),
                        } as DropdownOption)
                    )}
                    car={car}
                    selected={selectedCarIndexes.includes(car.id)}
                    onSelectChange={(selected: boolean) => {
                      if (selected) {
                        setSelectedCarIndexes((prev) => [...prev, car.id]);
                      } else {
                        setSelectedCarIndexes((prev) =>
                          prev.filter((id) => id !== car.id)
                        );
                      }
                    }}
                    index={car.id}
                    onChange={handleCarDetailsFormChange}
                    onValidChange={handleCarDetailsFormValidChange}
                    onAddNewLocation={() => {
                      setCurrentStep(Step.ADD_LOCATION);
                    }}
                    style={styles.carDetailsForm}
                    defaultValues={carDetailsFormValues[car.id]}
                  />
                );
              })}
            </ScrollView>
          ) : (
            <>
              {cars.map((car) => {
                const trimField = trimFieldForCar(car);

                return (
                  <CarDetailsForm
                    key={car.id}
                    pickupLocationOptions={carDetailOptions.pickupLocations.map(
                      (loc) => {
                        return {
                          id: String(loc.id),
                          value: String(loc.id),
                          title: loc.address,
                        } as DropdownOption;
                      }
                    )}
                    trimOptions={trimField.options}
                    trimLocked={trimField.locked}
                    licensePlateStateOptions={Object.entries(
                      carDetailOptions.licensePlateStates
                    ).map(
                      ([state, idx]) =>
                        ({
                          id: String(idx),
                          value: state,
                          title:
                            state.charAt(0).toUpperCase() +
                            state.slice(1).replace(/([A-Z])/g, ' $1'),
                        } as DropdownOption)
                    )}
                    car={car}
                    selected={selectedCarIndexes.includes(car.id)}
                    onSelectChange={(selected: boolean) => {
                      if (selected) {
                        setSelectedCarIndexes((prev) => [...prev, car.id]);
                      } else {
                        setSelectedCarIndexes((prev) =>
                          prev.filter((id) => id !== car.id)
                        );
                      }
                    }}
                    index={car.id}
                    onChange={handleCarDetailsFormChange}
                    onValidChange={handleCarDetailsFormValidChange}
                    onAddNewLocation={() => {
                      setCurrentStep(Step.ADD_LOCATION);
                    }}
                    style={styles.carDetailsForm}
                    defaultValues={carDetailsFormValues[car.id]}
                  />
                );
              })}
            </>
          )}
        </View>
      );
    }

    if (currentStep === Step.PRICING) {
      return (
        <View style={styles.pricingContainer}>
          <View style={{ gap: 8 }}>
            <Text type="title2">Choose Models to Adjust Pricing</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Set your minimum and maximum daily rates. Our pricing AI adjusts
              prices within this range to help maximize revenue
            </Text>

            <Button
              text="How Does Pricing Work?"
              type="primarySmall"
              onPress={() => {
                setShowHowAutomatedPricingWorksModal(true);
              }}
              style={{
                width: 230,
              }}
            />
          </View>

          {selectedCars.map((car) => {
            return (
              <CarPricingForm
                key={car.id}
                car={car}
                index={car.id}
                projectedMonthlyIncome={
                  car.model &&
                  projectedMonthlyIncomes?.[snakeToCamelCase(car.model.name)]
                }
                onChange={handleCarPricingFormChange}
                onValidChange={handleCarPricingFormValidChange}
                style={styles.carPricingForm}
                defaultValues={carPricingFormValues[car.id]}
              />
            );
          })}
        </View>
      );
    }

    return <View />;
  };

  if (
    (currentStep === Step.BULK_LIST && isPendingBulkListDefaults) ||
    (currentStep === Step.SELECT_CARS &&
      (isLoadingCarsNeedSetup || isLoadingCarDetailOptions))
  ) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
      }}
    >
      {!isDesktop() ? (
        <Stack.Screen
          options={{
            title: 'Bulk List Cars',
            headerTitle: selectedStep?.title,
            headerLeft: () => (
              <BackButton
                onPress={() => {
                  if (currentStep === Step.PRICING) {
                    setCurrentStep(Step.SELECT_CARS);
                    return;
                  }

                  if (currentStep === Step.SELECT_CARS) {
                    setCurrentStep(Step.BULK_LIST);
                    return;
                  }

                  if (currentStep === Step.ADD_LOCATION) {
                    setCurrentStep(Step.BULK_LIST);
                    return;
                  }

                  router.navigate('/partner/cars');
                }}
              />
            ),
          }}
        />
      ) : null}

      <View style={styles.outsideContainer}>
        <View style={styles.nav}>
          <NavigationButton
            type={NavigationButtonTypes.back_with_text}
            onPress={() => {
              if (currentStep === Step.PRICING) {
                setCurrentStep(Step.SELECT_CARS);
                return;
              }

              if (currentStep === Step.SELECT_CARS) {
                setCurrentStep(Step.BULK_LIST);
                return;
              }

              if (currentStep === Step.ADD_LOCATION) {
                setCurrentStep(Step.BULK_LIST);
                return;
              }

              router.navigate('/partner/cars');
            }}
          />

          <View style={{ gap: 4 }}>
            <Text type="title1">{selectedStep?.title}</Text>
          </View>
        </View>

        <ProgressIndicator currentStep={currentStep} numSteps={7} />

        <View style={styles.container}>
          <View style={{ flex: 1, display: 'flex', minHeight: 0, gap: 16 }}>
            <KeyboardAwareScrollView
              ref={scrollRef}
              style={{ flex: 1, minHeight: 0 }}
              contentContainerStyle={{
                flexGrow: 1,
                paddingBottom: 50,
                gap: 16,
              }}
              keyboardShouldPersistTaps="always"
              showsVerticalScrollIndicator={isDesktop() ? true : false}
            >
              {renderBody()}

              {isDesktop() ? (
                <View style={{ height: 50 }}>
                  <Button
                    text={currentStep === Step.PRICING ? 'List Cars' : 'Next'}
                    type="primary"
                    onPress={handleNext}
                    style={styles.button}
                    disabled={
                      currentStep === Step.BULK_LIST
                        ? !bulkListFormValid
                        : currentStep === Step.ADD_LOCATION
                        ? (showAddLocationForm && !addLocationFormValid) ||
                          (!showAddLocationForm && !pickupLocation)
                        : currentStep === Step.SELECT_CARS
                        ? !allCarDetailsFormsValid ||
                          selectedCarIndexes.length === 0
                        : currentStep === Step.PRICING
                        ? !allCarPricingFormsValid
                        : true
                    }
                    loading={isBulkListingCars}
                  />
                </View>
              ) : null}
            </KeyboardAwareScrollView>

            {!isDesktop() ? (
              <View style={{ height: 50 }}>
                <Button
                  text={currentStep === Step.PRICING ? 'List Cars' : 'Next'}
                  type="primary"
                  onPress={handleNext}
                  style={styles.button}
                  disabled={
                    currentStep === Step.BULK_LIST
                      ? !bulkListFormValid
                      : currentStep === Step.ADD_LOCATION
                      ? (showAddLocationForm && !addLocationFormValid) ||
                        (!showAddLocationForm && !pickupLocation)
                      : currentStep === Step.SELECT_CARS
                      ? !allCarDetailsFormsValid ||
                        selectedCarIndexes.length === 0
                      : currentStep === Step.PRICING
                      ? !allCarPricingFormsValid
                      : true
                  }
                  loading={isBulkListingCars}
                />
              </View>
            ) : null}
          </View>
        </View>
      </View>

      <WhyGloveboxPinModal
        visible={showWhyGloveboxPinModal}
        onClose={() => {
          setShowWhyGloveboxPinModal(false);
        }}
      />

      <HowAutomatedPricingWorksModal
        visible={showHowAutomatedPricingWorksModal}
        onClose={() => {
          setShowHowAutomatedPricingWorksModal(false);
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  outsideContainer: {
    flex: 1,
    paddingTop: {
      xs: 0,
      md: 56,
    },
    paddingLeft: {
      xs: 0,
      md: 56,
    },
    paddingRight: {
      xs: 0,
      md: 56,
    },
    paddingBottom: {
      xs: 0,
      md: 56,
    },
    gap: {
      xs: 0,
      md: 32,
    },
    width: '100%',
  },
  container: {
    flex: 1,
    paddingTop: {
      xs: 24,
      md: 0,
    },
    paddingLeft: {
      xs: 16,
      md: 0,
    },
    paddingRight: {
      xs: 16,
      md: 0,
    },
    paddingBottom: {
      xs: 24,
      md: 0,
    },
    gap: {
      xs: 16,
      md: 16,
    },
    width: '100%',
  },
  body: {
    flex: 1,
    gap: {
      xs: 56,
      md: 32,
    },
  },
  addCarsMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    position: {
      xs: 'absolute',
    },
    bottom: {
      xs: 0,
    },
    flexDirection: 'row',
  },
  inputContainer: {
    width: {
      xs: '100%',
      md: 400,
    },
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
  carDetailsFormContainer: {
    width: '100%',
    gap: 16,
    flexDirection: 'column',
  },
  carDetailsForm: {
    width: '100%',
  },
  pickupLocationDropdown: {
    width: {
      xs: '100%',
      md: 450,
    },
  },
  gloveboxPinButton: {
    paddingLeft: 0,
    paddingRight: 0,
    width: {
      xs: '100%',
      md: 360,
    },
    justifyContent: {
      md: 'flex-start',
    },
  },
  pricingContainer: {
    flex: 1,
    gap: 32,
    maxWidth: {
      xs: '100%',
      md: '100%',
    },
  },
  carPricingForm: {
    width: {
      xs: '100%',
      md: '80%',
      lg: '65%',
    },
  },
  subscriptionContainer: {
    gap: 24,
    maxWidth: {
      xs: '100%',
      md: 700,
    },
  },
}));
